import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import { FileShare, GroupMember } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

interface FileShareModalProps {
    fileId: string;
    fileName?: string;
    isOpen: boolean;
    onClose: () => void;
}

interface ShareFormState {
    selectedMemberUuids: string[];
}

const defaultFormState: ShareFormState = {
    selectedMemberUuids: [],
};

const FileShareModal: React.FC<FileShareModalProps> = ({ fileId, fileName, isOpen, onClose }) => {
    const [shares, setShares] = useState<FileShare[]>([]);
    const [members, setMembers] = useState<GroupMember[]>([]);
    const [formState, setFormState] = useState<ShareFormState>(defaultFormState);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isRevokingShareId, setIsRevokingShareId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [memberSearch, setMemberSearch] = useState('');

    const apiService = useMemo(() => new ApiService(), []);
    const { user } = useAuth();
    const { t } = useTranslation();

    useEffect(() => {
        if (!isOpen || !fileId) {
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const [shareList, teamMembers] = await Promise.all([
                    apiService.getFileShares(fileId),
                    apiService.getTeamGroupUsers()
                ]);

                setShares(shareList);
                setMembers(teamMembers.members);
            } catch (err) {
                logger.error('Error loading share modal data:', err);
                setError(t('fileShareModal.errors.loadFailed'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [apiService, fileId, isOpen, t]);

    useEffect(() => {
        if (isOpen) {
            setFormState(defaultFormState);
            setSuccessMessage(null);
            setError(null);
            setMemberSearch('');
        }
    }, [isOpen]);

    const handleClose = () => {
        setFormState(defaultFormState);
        setSuccessMessage(null);
        setError(null);
        setMemberSearch('');
        onClose();
    };

    const activeShareRecipients = useMemo(() => {
        const recipients = new Set<string>();
        shares.forEach(share => {
            if (share.targetUserUuid && share.isActive !== false && share.status !== 'revoked') {
                recipients.add(share.targetUserUuid);
            }
        });
        return recipients;
    }, [shares]);

    const currentUserIdentifiers = useMemo(() => {
        const identifiers = new Set<string>();
        if (user?.username) {
            identifiers.add(user.username.toLowerCase());
        }
        if (user?.email) {
            identifiers.add(user.email.toLowerCase());
        }
        return identifiers;
    }, [user?.username, user?.email]);

    const selectableMembers = useMemo(() => members.filter(member => {
        const username = member.username?.toLowerCase();
        if (username && currentUserIdentifiers.has(username)) {
            return false;
        }

        const email = member.email?.toLowerCase();
        if (email && currentUserIdentifiers.has(email)) {
            return false;
        }

        return true;
    }), [members, currentUserIdentifiers]);

    const filteredMembers = useMemo(() => {
        const searchTerm = memberSearch.trim().toLowerCase();
        if (!searchTerm) {
            return selectableMembers;
        }

        return selectableMembers.filter(member => {
            const username = member.username?.toLowerCase() || '';
            const email = member.email?.toLowerCase() || '';
            return username.includes(searchTerm) || email.includes(searchTerm);
        });
    }, [memberSearch, selectableMembers]);

    const toggleMemberSelection = (memberUuid: string) => {
        if (activeShareRecipients.has(memberUuid)) {
            return;
        }

        setFormState(prev => {
            const isSelected = prev.selectedMemberUuids.includes(memberUuid);
            const selectedMemberUuids = isSelected
                ? prev.selectedMemberUuids.filter(uuid => uuid !== memberUuid)
                : [...prev.selectedMemberUuids, memberUuid];

            return {
                ...prev,
                selectedMemberUuids
            };
        });
    };

    const handleSelectAllFiltered = () => {
        const selectableMemberUuids = filteredMembers
            .map(member => member.userUuid)
            .filter((uuid): uuid is string => {
                if (!uuid) return false;
                return !activeShareRecipients.has(uuid);
            });

        setFormState(prev => {
            const combined = new Set<string>([...prev.selectedMemberUuids, ...selectableMemberUuids]);
            return {
                ...prev,
                selectedMemberUuids: Array.from(combined)
            };
        });
    };

    const handleClearSelection = () => {
        setFormState(prev => ({ ...prev, selectedMemberUuids: [] }));
    };

    const handleCreateShare = async (event: React.FormEvent) => {
        event.preventDefault();

        const selectedTargets = formState.selectedMemberUuids.filter(Boolean);

        if (selectedTargets.length === 0) {
            setError(t('fileShareModal.errors.selectMembers'));
            return;
        }

        if (!fileId) {
            setError(t('fileShareModal.errors.missingIdentifierShare'));
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const results = await Promise.allSettled(selectedTargets.map(targetUuid =>
                apiService.createFileShare(fileId, {
                    targetType: 'user',
                    targetUserUuid: targetUuid,
                    permission: 'manage',
                })
            ));

            const successfulShares: FileShare[] = [];
            const failedTargets: string[] = [];

            results.forEach((result, index) => {
                const targetUuid = selectedTargets[index];
                if (result.status === 'fulfilled') {
                    successfulShares.push(result.value);
                } else {
                    logger.error('Error creating file share:', result.reason);
                    failedTargets.push(targetUuid);
                }
            });

            if (successfulShares.length > 0) {
                try {
                    const refreshedShares = await apiService.getFileShares(fileId);
                    setShares(refreshedShares);
                } catch (refreshError) {
                    logger.error('Error refreshing shares after creation:', refreshError);
                    setShares(prev => {
                        const map = new Map<number, FileShare>();
                        [...prev, ...successfulShares].forEach(share => {
                            map.set(share.id, share);
                        });
                        return Array.from(map.values());
                    });
                }

                const successCount = successfulShares.length;
                setSuccessMessage(t('fileShareModal.shareSuccess', { count: successCount }));
            }

            if (failedTargets.length > 0) {
                const failedNames = failedTargets.map(uuid => {
                    const member = members.find(m => m.userUuid === uuid);
                    return member?.username || member?.email || uuid;
                }).join(', ');
                setError(t('fileShareModal.errors.shareFailedFor', { names: failedNames }));
            }

            setFormState(prev => ({
                ...prev,
                selectedMemberUuids: failedTargets,
            }));

            if (failedTargets.length === 0) {
                setMemberSearch('');
            }
        } catch (err) {
            logger.error('Error creating file shares:', err);
            setError(t('fileShareModal.errors.shareFailed'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleRevokeShare = async (shareId: number) => {
        if (!fileId) {
            setError(t('fileShareModal.errors.missingIdentifierUpdate'));
            return;
        }

        setIsRevokingShareId(shareId);
        setError(null);
        setSuccessMessage(null);

        try {
            const revokedShare = await apiService.revokeFileShare(fileId, shareId);
            setShares(prev => prev.map(share => share.id === revokedShare.id ? revokedShare : share));
            setSuccessMessage(t('fileShareModal.revokeSuccess'));
        } catch (err) {
            logger.error('Error revoking file share:', err);
            setError(t('fileShareModal.errors.revokeFailed'));
        } finally {
            setIsRevokingShareId(null);
        }
    };

    const getShareTargetLabel = (share: FileShare): string => {
        if (share.targetName) {
            return share.targetName;
        }

        if (share.targetType === 'user' && share.targetUserUuid) {
            const member = members.find(m => m.userUuid === share.targetUserUuid);
            if (member) {
                return member.username;
            }
        }

        if (share.targetEmail) {
            return share.targetEmail;
        }

        if (share.targetIdentifier) {
            return share.targetIdentifier;
        }

        return t('fileShareModal.unknownRecipient');
    };

    const selectedCount = formState.selectedMemberUuids.length;
    const selectableMemberUuidsInView = filteredMembers.reduce<string[]>((acc, member) => {
        if (member.userUuid && !activeShareRecipients.has(member.userUuid)) {
            acc.push(member.userUuid);
        }
        return acc;
    }, []);
    const hasSelectableMembersInView = selectableMemberUuidsInView.length > 0;
    const allSelectableInViewSelected = hasSelectableMembersInView
        ? selectableMemberUuidsInView.every(uuid => formState.selectedMemberUuids.includes(uuid))
        : true;

    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">{t('fileShareModal.title')}</h2>
                        {fileName && (
                            <p className="text-sm text-gray-600">{fileName}</p>
                        )}
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                        aria-label={t('common.close')}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="px-6 py-4 overflow-y-auto">
                    {error && (
                        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    {successMessage && (
                        <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200 text-green-700 text-sm">
                            {successMessage}
                        </div>
                    )}

                    {isLoading ? (
                        <div className="flex items-center justify-center py-10">
                            <svg className="animate-spin h-6 w-6 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="ml-2 text-sm text-gray-600">{t('fileShareModal.loading')}</span>
                        </div>
                    ) : (
                        <>
                            <form onSubmit={handleCreateShare} className="bg-gray-50 rounded-md border border-gray-200 p-4 mb-6">
                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-1">
                                        <h3 className="text-sm font-medium text-gray-900">{t('fileShareModal.shareWithTeam')}</h3>
                                        <span className="text-xs text-gray-500">{t('fileShareModal.selectedCount', { count: selectedCount })}</span>
                                    </div>

                                    <div className="flex flex-col gap-3">
                                        <div className="flex flex-col md:flex-row md:items-center md:gap-2">
                                            <div className="relative flex-1">
                                                <input
                                                    type="text"
                                                    value={memberSearch}
                                                    onChange={(event) => setMemberSearch(event.target.value)}
                                                    placeholder={t('fileShareModal.searchPlaceholder')}
                                                    className="block w-full rounded-md border border-gray-300 py-2 pl-3 pr-3 text-sm focus:border-primary-500 focus:ring-primary-500"
                                                />
                                            </div>
                                            <div className="flex items-center gap-2 mt-2 md:mt-0">
                                                <button
                                                    type="button"
                                                    onClick={handleSelectAllFiltered}
                                                    disabled={!hasSelectableMembersInView || allSelectableInViewSelected}
                                                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-primary-200 text-primary-700 bg-primary-50 hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {t('fileShareModal.selectVisible')}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleClearSelection}
                                                    disabled={selectedCount === 0}
                                                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {t('fileShareModal.clearSelection')}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="border border-gray-200 rounded-md bg-white max-h-56 overflow-y-auto">
                                            {filteredMembers.length === 0 ? (
                                                <div className="px-3 py-4 text-sm text-gray-500">
                                                    {t('fileShareModal.noMembersFound')}
                                                </div>
                                            ) : (
                                                filteredMembers.map(member => {
                                                    const uuid = member.userUuid;
                                                    const isDisabled = !uuid || activeShareRecipients.has(uuid);
                                                    const isSelected = !!uuid && formState.selectedMemberUuids.includes(uuid);

                                                    return (
                                                        <label
                                                            key={uuid || member.username || member.email}
                                                            className={`flex items-center justify-between px-3 py-2 border-b border-gray-100 last:border-b-0 ${isDisabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:bg-primary-50 cursor-pointer'}`}
                                                        >
                                                            <div className="flex items-center">
                                                                <input
                                                                    type="checkbox"
                                                                    className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                                                                    checked={isSelected}
                                                                    onChange={() => uuid && toggleMemberSelection(uuid)}
                                                                    disabled={isDisabled}
                                                                />
                                                                <div className="ml-3">
                                                                    <div className={`text-sm ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>
                                                                        {member.username || member.email || t('fileShareModal.unknownUser')}
                                                                    </div>
                                                                    {member.email && member.email !== member.username && (
                                                                        <div className="text-xs text-gray-500">{member.email}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {isDisabled && (
                                                                <span className="ml-3 text-xs font-medium text-gray-500">{t('fileShareModal.alreadyShared')}</span>
                                                            )}
                                                        </label>
                                                    );
                                                })
                                            )}
                                        </div>

                                        <p className="text-xs text-gray-500">
                                            {t('fileShareModal.selectHint')}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-4 flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={isSaving || selectedCount === 0}
                                        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                                    >
                                        {isSaving && (
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        )}
                                        {t('common.share')}
                                    </button>
                                </div>
                            </form>

                            <div>
                                <h3 className="text-sm font-medium text-gray-900 mb-3">{t('fileShareModal.currentShares')}</h3>

                                {shares.length === 0 ? (
                                    <p className="text-sm text-gray-500">{t('fileShareModal.notSharedYet')}</p>
                                ) : (
                                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th scope="col" className="px-4 py-2 text-left font-medium text-gray-600">{t('fileShareModal.columns.recipient')}</th>
                                                    <th scope="col" className="px-4 py-2 text-left font-medium text-gray-600">{t('fileShareModal.columns.status')}</th>
                                                    <th scope="col" className="px-4 py-2 text-right font-medium text-gray-600">{t('fileShareModal.columns.actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {shares.map(share => {
                                                    const isRevoked = !!share.revokedAt || share.status === 'revoked';
                                                    const isActive = !isRevoked;

                                                    return (
                                                        <tr key={share.id} className="bg-white">
                                                            <td className="px-4 py-2 text-gray-900">
                                                                <div className="font-medium">
                                                                    {getShareTargetLabel(share)}
                                                                </div>
                                                                {share.targetEmail && (
                                                                    <div className="text-xs text-gray-500">{share.targetEmail}</div>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-2">
                                                                {isActive ? (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                                                        {t('fileShareModal.statusActive')}
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                                                        {t('fileShareModal.statusRevoked')}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-2 text-right">
                                                                {isActive ? (
                                                                    <button
                                                                        onClick={() => handleRevokeShare(share.id)}
                                                                        disabled={isRevokingShareId === share.id}
                                                                        className="inline-flex items-center px-3 py-1 border border-red-200 text-sm font-medium rounded-md text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                                                    >
                                                                        {isRevokingShareId === share.id && (
                                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                            </svg>
                                                                        )}
                                                                        {t('fileShareModal.revoke')}
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-xs text-gray-400">{t('fileShareModal.noActionsAvailable')}</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={handleClose}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        {t('common.close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FileShareModal;
